<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || providerLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !providerLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12">
          <h4 class="secondary300--text">{{ $t('public-contact') }}</h4>
        </v-col>
      </v-row>
      <v-row v-for="(publicContact, i) in provider.publicContacts" :key="i">
        <v-col
          cols="12"
          id="verify-email"
          class="primary400--text pt-4 pb-4"
          v-if="publicContact.id && !publicContact.verified"
        >
          <v-row>
            <v-col cols="9" class="d-flex flex-row justify-left align-center">
              <b>The user has not been verified.</b>
            </v-col>
            <v-col cols="3" class="d-flex flex-row justify-end">
              <v-btn
                depressed
                rounded
                @click="resendVerification(publicContact)"
                outlined
                class="primary200--text font-weight-bold"
                style="background: white"
                data-cy="submit"
              >
                {{ 'Resend Verification' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('email') }}</label>
          <p class="label-info">{{ $t('email-public-message') }}</p>
          <div class="d-flex flex-row justify-center">
            <v-text-field
              v-model="publicContact.email"
              :placeholder="$t('email')"
              outlined
              :disabled="!edit"
              class="required"
              :rules="requiredRule()"
              data-cy="email"
              v-on:keydown.enter.prevent
            ></v-text-field>
            <v-btn v-if="!next && edit" fab small text @click="removePublicContact(i)" class="ml-2">
              <v-icon>
                $vuetify.icons.trash
              </v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('firstname') }}</label>
                    <p class="label-info">{{ $t('firstname-public-message') }}</p>
                    <v-text-field
                      v-model="publicContact.firstname"
                      :placeholder="$t('firstname')"
                      outlined
                      :disabled="!edit"
                      data-cy="firstName"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('lastname') }}</label>
                    <p class="label-info">{{ $t('lastname-public-message') }}</p>
                    <v-text-field
                      v-model="publicContact.lastname"
                      :placeholder="$t('lastname')"
                      outlined
                      :disabled="!edit"
                      data-cy="lastName"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('phone') }}</label>
                    <p class="label-info">{{ $t('phone-public-message') }}</p>
                    <v-text-field
                      v-model="publicContact.phone"
                      :placeholder="$t('phone')"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="phone"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('position') }}</label>
                    <p class="label-info">{{ $t('position-public-message') }}</p>
                    <v-text-field
                      v-model="publicContact.position"
                      :placeholder="$t('position')"
                      outlined
                      :disabled="!edit"
                      clearable
                      data-cy="main-contact-position"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row class="pt-0 links" v-if="!next && edit">
        <v-col cols="12">
          <v-btn tile text @click="addPublicContact()">
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-public-contact') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next && edit">
      <v-col cols="6" class="d-flex justify-end">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="back()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          depressed
          rounded
          x-large
          color="primary200"
          @click="add()"
          :disabled="!valid || !provider.publicContacts.length"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('next') }} <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-10" v-if="edit && !next">
      <v-col cols="6" class="d-flex justify-end" v-if="!admin">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="cancel()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-col>
      <v-col :cols="admin ? '12' : '6'" :class="admin ? 'text-center' : ''">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid || !provider.publicContacts.length"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('update') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'ProviderContactInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -1,
      unsavedChanges: false,
      provider: {
        id: null,
        mainContact: {},
        publicContacts: [],
      },
      providerLoading: true,
    };
  },
  mounted() {
    this.loadingComponents++;
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
    },
    back() {
      console.log('back!');
      this.$emit('tab', 2);
    },
    resendVerification(publicContact) {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/provider-settings/resend-verification', { id: publicContact.id })
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: 'Verification sent it successfully',
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    add() {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/provider-settings/contact-info', this.provider)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('provider-updated'),
          });
          this.unsavedChanges = false;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
          this.$emit('tab', 4);
        });
    },
    addPublicContact() {
      this.provider.publicContacts.push({
        email: null,
        firstname: null,
        lastname: null,
        phone: null,
        position: null,
      });
    },
    removePublicContact(index) {
      this.provider.publicContacts.splice(index, 1);
    },
    getProvider(id) {
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          this.provider = res.data;
          if (!this.provider.publicContacts.length) {
            this.addPublicContact();
            this.$nextTick(() => {
              this.unsavedChanges = false;
            });
          }
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      let id = this.$route.params.id;
      if (value === false) {
        if (sessionStorage.getItem('providerId')) {
          this.getProvider(sessionStorage.getItem('providerId'));
        } else if (id != null) {
          this.getProvider(id);
        } else {
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        }
      }
    },
    provider: {
      handler: function(val, oldVal) {
        console.log('[Provider] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
